import { computed, ref } from 'vue'

/**
 * Installazione del portale come app (PWA).
 *
 * Lo stato è a livello di modulo (singleton): `beforeinstallprompt` scatta una volta sola, al
 * caricamento, molto prima che il menu profilo sia montato, quindi `initPwaInstall()` va
 * chiamata all'avvio (main.ts) e il menu legge solo lo stato.
 */

/** Evento non ancora nei tipi DOM standard. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>
}

const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const installed = ref(false)
let initialized = false

/** True se l'app gira già come app installata (standalone / iOS «Aggiungi a Home»). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (window.matchMedia?.('(display-mode: standalone)').matches) return true
  } catch { /* matchMedia assente */ }
  return (navigator as Navigator & { standalone?: boolean }).standalone === true
}

/** True su iPhone/iPad (Safari non ha beforeinstallprompt: si installa da «Condividi»). */
export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  const iPadOs = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1
  return /iPad|iPhone|iPod/.test(ua) || iPadOs
}

export function initPwaInstall(): void {
  if (initialized || typeof window === 'undefined') return
  initialized = true
  installed.value = isStandalone()
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault() // niente mini-infobar: l'invito è il pulsante nel menu profilo
    deferredPrompt.value = e as BeforeInstallPromptEvent
  })
  window.addEventListener('appinstalled', () => {
    installed.value = true
    deferredPrompt.value = null
  })
}

/** Solo per i test: riporta il modulo allo stato iniziale. */
export function _resetPwaInstallForTests(): void {
  deferredPrompt.value = null
  installed.value = false
  initialized = false
}

export function usePwaInstall() {
  /** Il browser offre l'installazione (Chrome/Edge/Android) e l'app non è già installata. */
  const canInstall = computed(() => !installed.value && deferredPrompt.value !== null)
  /** iOS, non ancora installata: niente prompt nativo, si mostra il suggerimento manuale. */
  const showIosHint = computed(() => !installed.value && deferredPrompt.value === null && isIos())
  /** Il pulsante «Installa app» va mostrato (prompt nativo oppure suggerimento iOS). */
  const visible = computed(() => canInstall.value || showIosHint.value)

  async function install(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    const ev = deferredPrompt.value
    if (!ev) return 'unavailable'
    // L'evento si può usare una sola volta, qualunque sia la scelta.
    deferredPrompt.value = null
    try {
      await ev.prompt()
      const { outcome } = await ev.userChoice
      if (outcome === 'accepted') installed.value = true
      return outcome
    } catch {
      return 'dismissed'
    }
  }

  return { canInstall, showIosHint, visible, installed, install }
}
